import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { appConfirmMessage } from '../../../utils/appConfirm';
import { FaEdit, FaEye, FaEnvelope, FaPlus, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import '../styles/Invoice.css';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { patchAdminSearch } from '../../../utils/adminDashboardNav';
import {
  useProfileBookingId,
  useProfileProjectId,
  useProfileRefresh,
} from '../ProfileBookingContext';
import { formatAUD } from '../../../utils/formatCurrency';
import { getInvoiceDisplayStatusMeta } from '../../../utils/paymentStatus';
import {
  getLocalEmailSignature,
  isBlankSignatureHtml,
} from './emailSignatureStorage';
import EditInvoiceModal from './EditInvoiceModal';
import { invoiceEmailDraft } from './QuotationSendEmail';
import ViewInvoice from '../../ViewInvoice';
import CustomInvoicePanel from '../../ProjectSection/components/CustomInvoicePanel';

const SEND_INVOICE_EMAIL = `
  mutation SendMailInvoice($bookingId: String!, $info: String!, $invoiceId: String) {
    sendMailInvoice(
      bookingId: $bookingId,
      info: $info,
      invoiceId: $invoiceId
    )
  }
`;

const FETCH_BOOKING_INVOICES = `
  query FindBookingInvoice($bookingId: String!, $projectId: String) {
    findBookingInvoice(bookingId: $bookingId, projectId: $projectId) {
      id
      invoiceNumber
      paymentStatus
      bookingId
      projectId
      createdAt
      invoiceStage
      listCustomerName
      listCustomerEmail
      listProjectCode
      listProjectName
      listServiceCount
      listTotal
      listInvoiceKind
    }
  }
`;

const FETCH_INVOICE_DETAIL = `
  query FindOneBookingInvoice($id: String!) {
    findOneBookingInvoice(id: $id) {
      id
      invoiceNumber
      paymentStatus
      bookingId
      projectId
      invoiceStage
      createdAt
      rawData
    }
  }
`;

const DELETE_INVOICE = `
  mutation DeleteInvoice($invoiceId: String!) {
    deleteInvoice(invoiceId: $invoiceId)
  }
`;

const FIND_LEAD_PROJECTS = `
  query LeadInvoiceProjects($bookingId: String!) {
    findBookingProject(bookingId: $bookingId) {
      id
      projectId
      name
      status
      createdAt
    }
  }
`;

function currentProjectId(rows) {
  const list = [...(rows || [])].sort(
    (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0),
  );
  const live = list.find((row) => {
    const key = String(row.status || '').toLowerCase();
    return key === 'active' || key === 'pending';
  });
  return (live || list[0])?.id || '';
}

function toDateInputValue(value) {
  if (!value) return '';
  const raw = String(value).trim();
  const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) return `${ymd[1]}-${ymd[2]}-${ymd[3]}`;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDaysToDateInput(value, days) {
  const iso = toDateInputValue(value);
  const base = iso
    ? new Date(
        Number(iso.slice(0, 4)),
        Number(iso.slice(5, 7)) - 1,
        Number(iso.slice(8, 10)),
      )
    : new Date();
  const d = new Date(
    base.getFullYear(),
    base.getMonth(),
    base.getDate() + days,
  );
  return toDateInputValue(d);
}

function isCustomInvoice(inv) {
  return String(inv?.listInvoiceKind || '').toUpperCase() === 'CUSTOM';
}

export function toViewInvoiceShape(detail) {
  let raw = detail?.rawData || {};
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw) || {};
    } catch {
      raw = {};
    }
  }
  return {
    ...raw,
    id: detail.id,
    invoiceNumber: detail.invoiceNumber,
    paymentStatus: detail.paymentStatus,
    invoiceStage: detail.invoiceStage,
    createdAt: detail.createdAt,
    dueDate: raw.dueDate || null,
    projectId: detail.projectId || raw.projectId || null,
    totalAmount:
      raw.totalAmount ??
      raw.services?.totalWithTax ??
      raw.services?.totalPrice ??
      null,
    bookingInfo: {
      ...(raw.bookingInfo || {}),
      id: detail.bookingId || raw.bookingInfo?.id,
    },
  };
}

function formatScheduleDate(iso) {
  const [y, m, d] = String(iso || '').split('-').map(Number);
  if (!y || !m || !d) return iso || '';
  return new Date(y, m - 1, d).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Invoice list for the current lead / project.
 *
 * Optional `schedule` (project Invoice Settings) adds a Schedule column —
 * Auto for invoices the schedule generated, Manual otherwise — plus the next
 * scheduled invoice as a row that opens the settings.
 *   { autoInvoiceIds: Set<string>, upcoming: { date, amount } | null, onManage() }
 */
export default function Invoice({ schedule = null } = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showViewInvoice, setShowViewInvoice] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [kindFilter, setKindFilter] = useState('all');
  const restoredInvoiceRef = useRef('');
  const invoiceParams = new URLSearchParams(location.search || '');
  const invoiceFromUrl = invoiceParams.get('invoice');
  const invoiceModeFromUrl = invoiceParams.get('mode');

  const writeInvoiceQuery = useCallback(
    (id, extra = {}) => {
      const search = patchAdminSearch(location.search, {
        invoice: id || '',
        mode: extra.mode || '',
      });
      if (search === (location.search || '')) return;
      navigate(
        { pathname: location.pathname || '/', search },
        { replace: true },
      );
    },
    [location.pathname, location.search, navigate],
  );

  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [sending, setSending] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [emailNote, setEmailNote] = useState({
    to: '',
    ...invoiceEmailDraft(),
    cc: '',
    dueDate: '',
  });

  const bookingId = useProfileBookingId();
  const contextProjectId = useProfileProjectId();
  const [leadProjects, setLeadProjects] = useState([]);
  const projectId = contextProjectId || currentProjectId(leadProjects);
  const { refreshToken, bumpRefresh } = useProfileRefresh();

  useEffect(() => {
    if (!bookingId || contextProjectId) {
      setLeadProjects([]);
      return undefined;
    }
    let live = true;
    graphqlRequest(FIND_LEAD_PROJECTS, { bookingId })
      .then((res) => {
        if (live) setLeadProjects(res?.findBookingProject || []);
      })
      .catch(() => {
        if (live) setLeadProjects([]);
      });
    return () => {
      live = false;
    };
  }, [bookingId, contextProjectId]);

  const fetchInvoices = useCallback(async () => {
    if (!bookingId) return;
    try {
      setLoading(true);
      const res = await graphqlRequest(
        FETCH_BOOKING_INVOICES,
        {
          bookingId,
          projectId: contextProjectId || null,
        },
        { timeout: 60000 },
      );
      const rows = Array.isArray(res.findBookingInvoice)
        ? res.findBookingInvoice
        : [];
      const seen = new Set();
      setInvoices(
        rows.filter((row) => {
          if (!row?.invoiceNumber || !row.id || seen.has(row.id)) return false;
          seen.add(row.id);
          return true;
        }),
      );
    } catch (err) {
      console.error(err);
      toast.error('Failed to load invoices');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [bookingId, contextProjectId]);

  useEffect(() => {
    if (!bookingId) return;
    fetchInvoices();
  }, [bookingId, refreshToken, fetchInvoices]);

  const visibleInvoices = useMemo(() => {
    if (kindFilter === 'custom') return invoices.filter(isCustomInvoice);
    if (kindFilter === 'regular') return invoices.filter((inv) => !isCustomInvoice(inv));
    return invoices;
  }, [invoices, kindFilter]);

  const openEditInvoice = async (inv, options = {}) => {
    if (!inv?.id) {
      toast.error('Invoice not found');
      return;
    }
    setShowViewInvoice(false);
    setSelectedInvoice(null);
    setLoadingDetail(true);
    try {
      const res = await graphqlRequest(FETCH_INVOICE_DETAIL, { id: inv.id });
      const detail = res.findOneBookingInvoice;
      if (!detail?.id) {
        toast.error('Invoice not found');
        return;
      }
      setEditInvoice({
        ...detail,
        bookingId: detail.bookingId || inv.bookingId || bookingId,
        projectId: detail.projectId || inv.projectId || projectId || null,
      });
      restoredInvoiceRef.current = `${detail.id}:edit`;
      if (!options.skipUrl) {
        writeInvoiceQuery(detail.id, { mode: 'edit' });
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Failed to load invoice for edit');
    } finally {
      setLoadingDetail(false);
    }
  };

  const closeInvoiceView = useCallback(() => {
    restoredInvoiceRef.current = '';
    setShowViewInvoice(false);
    setSelectedInvoice(null);
    writeInvoiceQuery(null);
  }, [writeInvoiceQuery]);

  const openInvoiceDetail = async (inv) => {
    if (!inv?.id) {
      toast.error('Invoice not found');
      return;
    }
    setLoadingDetail(true);
    try {
      const res = await graphqlRequest(FETCH_INVOICE_DETAIL, { id: inv.id });
      const detail = res.findOneBookingInvoice;
      if (!detail) {
        toast.error('Invoice not found');
        restoredInvoiceRef.current = '';
        writeInvoiceQuery(null);
        return;
      }
      restoredInvoiceRef.current = `${detail.id}:view`;
      setSelectedInvoice(toViewInvoiceShape(detail));
      setShowViewInvoice(true);
      writeInvoiceQuery(detail.id);
    } catch (err) {
      console.error(err);
      restoredInvoiceRef.current = '';
      toast.error('Failed to load invoice details');
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    if (!invoiceFromUrl) {
      restoredInvoiceRef.current = '';
      return;
    }
    const restoreKey = `${invoiceFromUrl}:${invoiceModeFromUrl === 'edit' ? 'edit' : 'view'}`;
    if (restoredInvoiceRef.current === restoreKey) return;
    restoredInvoiceRef.current = restoreKey;
    if (invoiceModeFromUrl === 'edit') {
      openEditInvoice({ id: invoiceFromUrl }, { skipUrl: true });
      return;
    }
    openInvoiceDetail({ id: invoiceFromUrl });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceFromUrl, invoiceModeFromUrl]);

  const openSendEmail = async (inv) => {
    setLoadingDetail(true);
    try {
      const res = await graphqlRequest(FETCH_INVOICE_DETAIL, { id: inv.id });
      const detail = res.findOneBookingInvoice;
      if (!detail) {
        toast.error('Invoice not found');
        return;
      }
      const shaped = toViewInvoiceShape(detail);
      const to =
        String(shaped.bookingInfo?.email || '').trim() ||
        String(inv.listCustomerEmail || '').trim();
      const issued = shaped.issuedAt || new Date();
      const storedDue = toDateInputValue(shaped.dueDate);
      const dueDate =
        storedDue && (shaped.issuedAt || storedDue >= toDateInputValue(issued))
          ? storedDue
          : addDaysToDateInput(issued, 15);
      setEmailTarget(shaped);
      setEmailNote((prev) => ({
        ...prev,
        ...invoiceEmailDraft(shaped.invoiceNumber),
        to,
        dueDate,
      }));
      setShowEmailPopup(true);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load invoice for email');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleDeleteInvoice = async (inv) => {
    const label = inv.invoiceNumber || inv.id;
    if (
      !await appConfirmMessage(
        `Delete invoice ${label}? This cannot be undone from the list.`,
      )
    ) {
      return;
    }
    setDeletingId(inv.id);
    try {
      await graphqlRequest(DELETE_INVOICE, { invoiceId: inv.id });
      toast.success(`Invoice ${label} deleted`);
      if (selectedInvoice?.id === inv.id) {
        closeInvoiceView();
      }
      if (editInvoice?.id === inv.id) {
        setEditInvoice(null);
      }
      fetchInvoices();
      bumpRefresh();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Failed to delete invoice');
    } finally {
      setDeletingId(null);
    }
  };

  const startRegularInvoice = () => {
    if (!bookingId) {
      toast.error('No booking linked');
      return;
    }
    if (!contextProjectId && !leadProjects.length) {
      toast.error('Create a project for this lead before a regular invoice.');
      return;
    }
    setShowViewInvoice(false);
    setSelectedInvoice(null);
    setEditInvoice({
      bookingId,
      projectId: contextProjectId || '',
      invoiceStage: 'DRAFT',
      rawData: {},
    });
  };

  const upcoming =
    schedule?.upcoming && kindFilter !== 'custom' ? schedule.upcoming : null;

  if (loading && !showViewInvoice) return <p>Loading invoices...</p>;

  const emptyFilterMessage =
    invoices.length === 0
      ? `No invoices yet for this ${projectId ? 'project' : 'lead'}.`
      : kindFilter === 'custom'
        ? 'No custom invoices for this project yet.'
        : kindFilter === 'regular'
          ? 'No regular invoices for this project yet.'
          : 'No invoices match this filter.';

  return (
    <div className="invoice-container">
      <div className="invoice-toolbar">
        <div className="invoice-kind-filter" role="tablist" aria-label="Invoice type">
          {[
            { id: 'all', label: 'All' },
            { id: 'regular', label: 'Regular' },
            { id: 'custom', label: 'Custom' },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              role="tab"
              aria-selected={kindFilter === opt.id}
              className={`invoice-kind-filter-btn${kindFilter === opt.id ? ' is-active' : ''}`}
              onClick={() => setKindFilter(opt.id)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="invoice-toolbar-actions">
          <button
            type="button"
            className="view-btn"
            disabled={!bookingId}
            onClick={startRegularInvoice}
            title="Open the regular invoice form."
          >
            <FaPlus /> Create regular invoice
          </button>
          <CustomInvoicePanel
            variant="create-only"
            bookingId={bookingId}
            projectId={contextProjectId || ''}
            projects={contextProjectId ? [] : leadProjects}
          />
        </div>
      </div>

      {!visibleInvoices.length && !upcoming ? (
        <p className="pd-muted">{emptyFilterMessage}</p>
      ) : (
      <div className="invoice-table-wrap">
      <table className="invoice-table">
        <thead>
          <tr>
            <th>Invoice No</th>
            <th>Type</th>
            <th>Stage</th>
            <th>Project</th>
            <th>Services</th>
            <th>Total</th>
            <th>Payment</th>
            {schedule ? <th>Schedule</th> : null}
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          {upcoming ? (
            <tr className="invoice-row--upcoming">
              <td>
                <span className="invoice-number">Next invoice</span>
                <br />
                <small>{formatScheduleDate(upcoming.date)}</small>
              </td>
              <td>
                <span className="invoice-kind-badge invoice-kind-badge--regular">
                  Regular
                </span>
              </td>
              <td>
                <span className="invoice-stage invoice-stage--scheduled">Scheduled</span>
              </td>
              <td>—</td>
              <td>—</td>
              <td>{formatAUD(upcoming.amount ?? 0)}</td>
              <td>
                <span className="status unpaid">Not sent</span>
              </td>
              <td>
                <span className="invoice-schedule invoice-schedule--auto">↻ Auto</span>
              </td>
              <td className="invoice-actions">
                <button
                  type="button"
                  className="invoice-manage-btn"
                  onClick={schedule.onManage}
                  title="Edit or skip this invoice from Invoice Settings"
                >
                  Manage
                </button>
              </td>
            </tr>
          ) : null}
          {visibleInvoices.map((inv) => {
            const statusMeta = getInvoiceDisplayStatusMeta(inv);
            const custom = isCustomInvoice(inv);
            return (
              <tr
                key={inv.id}
                className={custom ? 'invoice-row--custom' : undefined}
              >
                <td>
                  <span className="invoice-number">{inv.invoiceNumber}</span>
                </td>
                <td>
                  <span
                    className={`invoice-kind-badge ${
                      custom
                        ? 'invoice-kind-badge--custom'
                        : 'invoice-kind-badge--regular'
                    }`}
                  >
                    {custom ? 'Custom' : 'Regular'}
                  </span>
                </td>
                <td>
                  <span
                    className={`invoice-stage invoice-stage--${String(
                      inv.invoiceStage || 'FINAL',
                    ).toLowerCase()}`}
                  >
                    {inv.invoiceStage || 'FINAL'}
                  </span>
                </td>
                <td>
                  {inv.listProjectCode || inv.listProjectName || '—'}
                  {inv.listProjectCode && inv.listProjectName ? (
                    <>
                      <br />
                      <small>{inv.listProjectName}</small>
                    </>
                  ) : null}
                  {!inv.listProjectCode && !inv.listProjectName && inv.projectId ? (
                    <>
                      <br />
                      <small title={inv.projectId}>
                        {String(inv.projectId).slice(0, 8)}…
                      </small>
                    </>
                  ) : null}
                </td>
                <td>{inv.listServiceCount ?? 0}</td>
                <td>{formatAUD(inv.listTotal ?? 0)}</td>
                <td>
                  <span className={`status ${statusMeta.className}`}>
                    {statusMeta.label}
                  </span>
                </td>
                {schedule ? (
                  <td>
                    {schedule.autoInvoiceIds?.has(inv.id) ? (
                      <span className="invoice-schedule invoice-schedule--auto">↻ Auto</span>
                    ) : (
                      <span className="invoice-schedule">Manual</span>
                    )}
                  </td>
                ) : null}
                <td className="invoice-actions">
                  <div className="invoice-actions-row">
                    <button
                      type="button"
                      className="invoice-action-btn"
                      title="View"
                      aria-label="View invoice"
                      disabled={loadingDetail}
                      onClick={() => openInvoiceDetail(inv)}
                    >
                      <FaEye />
                    </button>
                    <button
                      type="button"
                      className="invoice-action-btn"
                      title="Edit invoice (GST, lines, bank). Resend email after saving a sent invoice."
                      aria-label="Edit invoice"
                      disabled={loadingDetail}
                      onClick={() => openEditInvoice(inv)}
                    >
                      <FaEdit />
                    </button>
                    <button
                      type="button"
                      className="invoice-action-btn"
                      title="Send email"
                      aria-label="Send invoice email"
                      disabled={loadingDetail || deletingId === inv.id}
                      onClick={() => openSendEmail(inv)}
                    >
                      <FaEnvelope />
                    </button>
                    <button
                      type="button"
                      className="invoice-action-btn invoice-action-btn--delete"
                      title="Delete invoice"
                      aria-label="Delete invoice"
                      disabled={loadingDetail || deletingId === inv.id}
                      onClick={() => handleDeleteInvoice(inv)}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      )}

      {showViewInvoice && selectedInvoice ? (
          <ViewInvoice
            invoice={selectedInvoice}
            bookingId={bookingId}
            onClose={() => {
              closeInvoiceView();
              fetchInvoices();
              bumpRefresh();
            }}
            onPaymentStatusChange={() => {
              fetchInvoices();
              bumpRefresh();
            }}
            onEdit={() =>
                    openEditInvoice({
                      id: selectedInvoice.id,
                      bookingId: selectedInvoice.bookingInfo?.id || bookingId,
                      projectId: selectedInvoice.projectId || projectId,
                    })
            }
          />
      ) : null}

      {editInvoice ? (
        <EditInvoiceModal
          invoice={editInvoice}
          projects={editInvoice?.id ? [] : leadProjects}
          onClose={() => {
            setEditInvoice(null);
            writeInvoiceQuery(null);
          }}
          onSaved={() => {
            fetchInvoices();
            bumpRefresh();
          }}
        />
      ) : null}

      {showEmailPopup && emailTarget && (
        <div className="email-overlay">
          <div className="email-composer">
            <div className="email-composer-header">
              <span>Send Invoice Email — {emailTarget.invoiceNumber}</span>
              <button
                type="button"
                className="email-close-btn"
                onClick={() => {
                  setShowEmailPopup(false);
                  setEmailTarget(null);
                }}
              >
                ✕
              </button>
            </div>

            <div className="email-row">
              <label>To</label>
              <input
                type="email"
                className="email-input"
                value={emailNote.to}
                onChange={(e) =>
                  setEmailNote((p) => ({ ...p, to: e.target.value }))
                }
                placeholder="client@email.com"
              />
            </div>

            <div className="email-row">
              <label>Subject</label>
              <input
                type="text"
                className="email-input"
                value={emailNote.subject}
                onChange={(e) =>
                  setEmailNote((p) => ({ ...p, subject: e.target.value }))
                }
              />
            </div>

            <div className="email-row">
              <label>CC</label>
              <input
                type="text"
                className="email-input"
                value={emailNote.cc}
                onChange={(e) =>
                  setEmailNote((p) => ({ ...p, cc: e.target.value }))
                }
              />
            </div>

            <div className="email-row">
              <label>Payment due date</label>
              <input
                type="date"
                className="email-input"
                value={emailNote.dueDate}
                onChange={(e) =>
                  setEmailNote((p) => ({ ...p, dueDate: e.target.value }))
                }
              />
              <p className="email-row-hint">
                Defaults to 15 days from the invoice date. The date you select
                is printed on the invoice.
              </p>
            </div>

            <div className="email-row">
              <label>Message</label>
              <textarea
                className="email-textarea"
                value={emailNote.message}
                onChange={(e) =>
                  setEmailNote((p) => ({ ...p, message: e.target.value }))
                }
              />
            </div>

            <div className="email-row">
              <label>Email signature</label>
              {!isBlankSignatureHtml(getLocalEmailSignature()) ? (
                <div
                  className="invoice-email-signature-preview"
                  dangerouslySetInnerHTML={{
                    __html: getLocalEmailSignature(),
                  }}
                />
              ) : (
                <p className="invoice-email-signature-hint">
                  No saved signature. Add one from Email → New Message → Edit
                  Signature.
                </p>
              )}
            </div>

            <div className="email-composer-actions">
              <button
                type="button"
                className="cancel-email-btn"
                onClick={() => {
                  setShowEmailPopup(false);
                  setEmailTarget(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="send-email-btn"
                disabled={sending}
                onClick={async () => {
                  const to = String(emailNote.to || '').trim();
                  if (!to) {
                    toast.error('Enter the client email');
                    return;
                  }
                  if (!emailNote.dueDate) {
                    toast.error('Select the payment due date');
                    return;
                  }
                  try {
                    setSending(true);
                    const savedSig = getLocalEmailSignature();
                    await graphqlRequest(
                      SEND_INVOICE_EMAIL,
                      {
                        bookingId,
                        info: JSON.stringify({
                          ...emailNote,
                          signature: isBlankSignatureHtml(savedSig)
                            ? undefined
                            : savedSig,
                        }),
                        invoiceId: emailTarget?.id || null,
                      },
                      { timeout: 120000 },
                    );
                    toast.success('Invoice email sent');
                    setShowEmailPopup(false);
                    setEmailTarget(null);
                    fetchInvoices();
                    bumpRefresh();
                  } catch (err) {
                    console.error(err);
                    toast.error(err?.message || 'Failed to send invoice');
                  } finally {
                    setSending(false);
                  }
                }}
              >
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
